import React from 'react';
import { Spinner } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useFormControl } from '@withjoy/joykit/components/FormControl/FormControl.provider';

import { PoweredByGoogle } from '@shared/components/PoweredByGoogle/PoweredByGoogle';
import { LocationInputV2SelectV1 } from './LocationInputV2SelectV1';
import type { OptionTypeV2 } from './LocationInputV2.types';
import type { LocationInputV2FieldProps } from './LocationInputV2.types';
import { useLocationInputV2 } from './LocationInputV2.controller';

const AUTOSELECT_DROPDOWN_HEIGHT = 315;
// On IOS browser auto select drop-down override on input field b/c of not have enough space. Minimized height in mobile
const AUTOSELECT_DROPDOWN_HEIGHT_MOBILE = 240;

export const LocationInputV2 = <T extends {}>(props: LocationInputV2FieldProps<T>) => {
  const { t } = useTranslation('joykit');
  const { noOptionsMessage = t('locationInput', 'noOptionsMessage')() } = props;
  const { options, inputValue, handleInputValue, handleChange, iconRight, iconLeft, onLocationOpen, onLocationClose, placeholder, isShowingSuggestions } =
    useLocationInputV2(props);
  const formControlProps = useFormControl({});
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });

  return (
    <LocationInputV2SelectV1
      noBorder={props.noBorder}
      suppressFocusRing={props.suppressFocusRing}
      placeholder={props.placeholderContent ?? (placeholder ? placeholder : 'San Francisco, CA')}
      options={options}
      inputValue={inputValue}
      noOptionsMessage={noOptionsMessage}
      searchable
      isInvalid={formControlProps.isInvalid}
      onChange={handleChange}
      onMenuOpen={onLocationOpen}
      onMenuClose={onLocationClose}
      optionFilter={() => true}
      iconRight={iconRight}
      iconLeft={iconLeft}
      onInputChange={handleInputValue}
      loadingMessage={() => <Spinner />}
      autoComplete="off"
      isHideListItemIcon={props.isHideListItemIcon}
      afterMenuContent={isShowingSuggestions ? undefined : <PoweredByGoogle />}
      maxDropdownHeight={props.maxDropdownHeight ? props.maxDropdownHeight : isMobile ? pxToRem(AUTOSELECT_DROPDOWN_HEIGHT_MOBILE) : pxToRem(AUTOSELECT_DROPDOWN_HEIGHT)}
      disabled={props.disabled}
      // Declared in the props type since this component was written, but never passed down, so
      // `ref.current` stayed null and every caller's focus() was a silent no-op. Matches v1.
      //
      // Cast because this renders the SelectV1 SUBCLASS, whose public `props` field narrows
      // `options` to a mutable array — enough for TS to reject the base-typed ref. The instance is
      // a SelectV1, so `inputRef` is exactly what the prop promises.
      ref={props.refInput as React.Ref<LocationInputV2SelectV1<OptionTypeV2>>}
    />
  );
};
